import { fetchFamilyInfo } from '../../family/familyInfoApi';
import { needsDisabilityFacts } from '../../disabilityFacts';
import { API_BASE_URL } from '../../../../../../config';
import type { TaxDeclarationProgressPayload } from '../taxDeclarationApi';
import { parseIncomeYen } from '../spouseSection/spouseEligibility';
import type { SingleParentCandidate } from './specialStatusTypes';

function isQualifyingRelationship(r: string): r is 'child' | 'grandchild' {
  const x = r.trim().toLowerCase();
  return x === 'child' || x === 'grandchild';
}

export async function fetchSingleParentCandidates(email: string): Promise<SingleParentCandidate[]> {
  const res = await fetchFamilyInfo(email);
  const members = res.members ?? [];
  return members
    .filter((m) => {
      const rel = (m.relationship ?? '').toLowerCase();
      return isQualifyingRelationship(rel) && m.claimTaxDependent === true && m.sharedLivelihood === true;
    })
    .map((m) => ({
      empFamilyId: m.id!,
      fullName: m.fullName?.trim() ?? '',
      relationship: (m.relationship ?? 'child').toLowerCase() as 'child' | 'grandchild',
      sharedLivelihood: true,
      claimedByOtherIncome: m.claimedByOtherIncome === true,
    }))
    .filter((c) => c.empFamilyId != null && c.fullName);
}

/** True when employee or any family member has disability (general/special). */
export async function fetchHasDisabilityContext(email: string): Promise<boolean> {
  const [familyRes, profileRes] = await Promise.all([
    fetchFamilyInfo(email),
    fetch(`${API_BASE_URL}/api/general-profile?email=${encodeURIComponent(email)}`).then(async (r) => {
      if (!r.ok) return null;
      return r.json() as Promise<{ values?: { disabilityCategory?: string } }>;
    }).catch(() => null),
  ]);
  const employeeCat = profileRes?.values?.disabilityCategory;
  if (needsDisabilityFacts(employeeCat)) return true;
  return (familyRes.members ?? []).some((m) => needsDisabilityFacts(m.disabilityCategory));
}

export function employeeIncomeFromProgress(data: TaxDeclarationProgressPayload | null): number {
  const raw = data?.declaration?.employeeEstimatedIncome;
  if (raw == null) return 0;
  if (typeof raw === 'number') return Math.trunc(raw);
  return parseIncomeYen(raw);
}

export function spouseChangeReason(data: TaxDeclarationProgressPayload | null): string {
  return data?.spouse?.changeReason?.trim() ?? '';
}
